// Single Source of Truth fuer Firmen-Farben — bento-spec-sprint6.md,
// Design-Wurf Sanierung 2026-07-03 (Fahrplan Phase 4).
//
// Markenfarben (Abgleich 2026-07-02 mit den Websites):
//   DrainX    -> Lime (Limettengruen auf Dunkelgruen, drainx.de)
//   G+K       -> Dunkelblau (gksanitaertechnik.de)
//   InfraX    -> Rot
//   X-Holding -> Indigo
//
// Design-Wurf 03.07.: Firmenfarbe FLAECHIG statt duenner Raender —
// linker Vollfarb-Balken (`accentBar`) + getoenter Header (`headerTint`)
// + kraeftigerer Karten-Gradient. Kevins Vorgabe: man muss auf einen
// Blick sehen, WESSEN Karte das ist.
//
// Severity-Trio (cyan/amber/pink) lebt in `lib/severity.ts` und darf NICHT
// auf Firmen-Tiles gemischt werden (Spec: Severity-King vs. Firma-Only).
// InfraX-Rot vs. Severity-Pink bleibt unterscheidbar: Firma = statischer
// Balken/Tint, Severity = Glow + Pulse.
//
// Tailwind-JIT: ALLE Klassen muessen hier als vollstaendige Literale
// stehen (kein String-Templating), sonst purged der Build sie weg.

import type { HoldingFirma } from "../services/firma_filter";

// InfraX wird im Holding-Frontend nicht konsolidiert (R1), aber die Farbe
// existiert fuer Detail-Pages / Anomaly-Stream / Petra-Calls etc., wo
// InfraX-Datenraum-spezifisch sichtbar sein darf.
export type FirmaSlug = HoldingFirma | "infrax";

export const ALL_FIRMA_SLUGS: readonly FirmaSlug[] = [
  "drainx",
  "gundk",
  "infrax",
  "xholding",
] as const;

export interface FirmaColorPalette {
  /** Tailwind border-Klasse (Per-Company-Card-Border, kraeftig). */
  border: string;
  /** Subtler Background-Tint (10% Alpha-Range). */
  bg: string;
  /** Stronger Background fuer aktive Hover/Selected. */
  bgStrong: string;
  /** Text/Fg-Tint fuer Labels innerhalb der Firmen-Card. */
  text: string;
  /** Reiner Dot/Indicator (z.B. `bg-lime-400` fuer 6px-Kreis). */
  dot: string;
  /** Gradient-Variante "from-… via-… to-transparent border-…" fuer
   *  Firmen-Tiles — Design-Wurf 03.07.: flaechig statt hauchduenn. */
  gradient: string;
  /** Linker Vollfarb-Balken (Design-Wurf 03.07.) — volle Markenfarbe. */
  accentBar: string;
  /** Getoenter Header-Streifen fuer Kartenkoepfe (Design-Wurf 03.07.). */
  headerTint: string;
  /** Hex-Wert der Markenfarbe fuer Inline-Styles/Charts. */
  hex: string;
}

export const FIRMA_COLORS: Record<FirmaSlug, FirmaColorPalette> = {
  drainx: {
    // Markenabgleich 2026-07-02 (drainx.de): Limettengruen auf Dunkelgruen.
    border: "border-lime-400/60",
    bg: "bg-lime-400/10",
    bgStrong: "bg-lime-400/20",
    text: "text-lime-300",
    dot: "bg-lime-400",
    gradient: "from-lime-400/[0.16] via-lime-400/[0.05] to-transparent border-lime-400/60",
    accentBar: "bg-lime-400",
    headerTint: "bg-lime-400/15",
    hex: "#a3e635",
  },
  gundk: {
    // Markenabgleich 2026-07-02 (gksanitaertechnik.de): Dunkelblau.
    border: "border-blue-600/60",
    bg: "bg-blue-600/10",
    bgStrong: "bg-blue-600/20",
    text: "text-blue-300",
    dot: "bg-blue-600",
    gradient: "from-blue-600/[0.16] via-blue-600/[0.05] to-transparent border-blue-600/60",
    accentBar: "bg-blue-600",
    headerTint: "bg-blue-600/15",
    hex: "#2563eb",
  },
  infrax: {
    border: "border-red-500/55",
    bg: "bg-red-500/10",
    bgStrong: "bg-red-500/20",
    text: "text-red-300",
    dot: "bg-red-500",
    gradient: "from-red-500/[0.14] via-red-500/[0.04] to-transparent border-red-500/55",
    accentBar: "bg-red-500",
    headerTint: "bg-red-500/15",
    hex: "#ef4444",
  },
  xholding: {
    border: "border-indigo-500/60",
    bg: "bg-indigo-500/10",
    bgStrong: "bg-indigo-500/20",
    text: "text-indigo-300",
    dot: "bg-indigo-500",
    gradient: "from-indigo-500/[0.16] via-indigo-500/[0.05] to-transparent border-indigo-500/60",
    accentBar: "bg-indigo-500",
    headerTint: "bg-indigo-500/15",
    hex: "#6366f1",
  },
};

/** Lookup mit defensivem Fallback fuer unbekannte slugs (defense-in-depth). */
export function firmaPalette(slug: string | null | undefined): FirmaColorPalette | null {
  if (!slug) return null;
  return (FIRMA_COLORS as Record<string, FirmaColorPalette>)[slug] ?? null;
}

/** Tailwind-Klasse fuer Firmen-Dot (z.B. "bg-lime-400"). Defensiver
 *  Fallback "bg-slate-400" fuer unbekannte slugs — verhindert dass Bugs
 *  als unsichtbare Dots maskiert werden. */
export function firmaDot(slug: string | null | undefined): string {
  return firmaPalette(slug)?.dot ?? "bg-slate-400";
}

/** Tailwind-Klasse fuer Firmen-Border (z.B. "border-lime-400/60"). */
export function firmaBorder(slug: string | null | undefined): string {
  return firmaPalette(slug)?.border ?? "border-slate-700/50";
}

/** Tailwind-Gradient-Klasse fuer Firmen-Tile-BG. */
export function firmaGradient(slug: string | null | undefined): string {
  return (
    firmaPalette(slug)?.gradient ?? "from-slate-800/10 to-transparent border-slate-700/30"
  );
}

/** Linker Vollfarb-Balken (Design-Wurf 03.07.). */
export function firmaAccentBar(slug: string | null | undefined): string {
  return firmaPalette(slug)?.accentBar ?? "bg-slate-500";
}

export const FIRMA_LABEL: Record<FirmaSlug, string> = {
  drainx: "DrainX",
  gundk: "G+K",
  infrax: "InfraX",
  xholding: "X-Holding",
};
